import { combineReducers, rm } from 'utils';
import { BaseAction, BaseInitialState, BaseState, baseFormReducer, baseReducer } from '../../Utils';
import {
  BaseZendeskFormAction,
  BaseZendeskFormState,
  Options,
  ZendeskWithProductsInitialState,
  addProductsReducer,
  baseZendeskFormReducer,
  validateProducts,
  validateQuestion,
  validateSubject
} from '../utils';
import {
  ImageAction,
  ImageInitialState,
  ImageState,
  imagesReducer
} from 'components/CustomerSupportForms/Components/ImageUpload/ImageHelper';

export const claimsFormId = 29744527470482;
export const subjectFieldId = '29545453284242';

const productFieldId = '29747016601490';
const deliveryFieldId = '29747035698834';

type InternalState = BaseState & BaseZendeskFormState & ImageState;

export const claimsInitialState: InternalState = {
  ...BaseInitialState,
  ...ZendeskWithProductsInitialState,
  ...ImageInitialState,
  showReason: false
};

type Action = BaseAction | BaseZendeskFormAction | ImageAction;

const claimsReducer = (state: InternalState, action: Action): InternalState => {
  switch (action.type) {
    case 'showReason':
      return { ...state, showReason: showReason(action.value) };
    default:
      return state;
  }
};

export const reducer = combineReducers<InternalState, Action>(
  baseFormReducer,
  baseReducer,
  baseZendeskFormReducer,
  addProductsReducer,
  claimsReducer,
  imagesReducer
);

const showReason = (subject: number) => {
  return subject > 0;
};

type ValidatedState = Omit<InternalState, 'subject' | 'products' | 'reason' | 'images'> &
  Required<Pick<InternalState, 'subject' | 'products' | 'reason' | 'images'>>;

export const isValid = (
  state: InternalState,
  dispatch: (action: Action) => void
): state is ValidatedState => {
  const isValidSubject = validateSubject(state, dispatch);
  const isValidQuestion = validateQuestion(state, dispatch);
  const isValidProducts = validateProducts(state, dispatch);

  const isInvalidReason = state.subject && state.reason === undefined;
  const isInvalidImages = !state.images.some(img => img.token !== '');

  if (isInvalidReason) {
    dispatch({
      type: 'reasonError',
      value: rm(
        'customerservice_shared_error_general_part',
        rm('customerservice_zendesk_reason').toLowerCase()
      )
    });
  }

  // At least one image is needed
  if (isInvalidImages) {
    dispatch({
      type: 'imagesError',
      value: rm('customerpage_returns_validation_error_images')
    });
  }

  return (
    isValidSubject && isValidQuestion && isValidProducts && !isInvalidReason && !isInvalidImages
  );
};

export const getSubjectOptions = (): Options[] => {
  return [
    { id: 1, tag: 'rac_product_claim' },
    { id: 2, tag: 'rac_transport_claim' }
  ];
};

export const getReasonOptions = (subject: number): Options[] => {
  if (subject === 1) {
    return getProductReasonOptions();
  }

  return getDeliveryReasonOptions();
};

export const getProductReasonOptions = (): Options[] => {
  return [
    { id: 1, tag: 'pc_defective_product' },
    { id: 2, tag: 'pc_missing_parts' },
    { id: 3, tag: 'pc_wrong_item_delivered' }
  ];
};

export const getDeliveryReasonOptions = (): Options[] => {
  return [
    { id: 1, tag: 'tc_missing_items_incomplete_delivery' },
    { id: 2, tag: 'tc_damage_in_delivery' },
    { id: 3, tag: 'tc_delivery_never_arrived' }
  ];
};

export const getReasonFieldId = (subject: number) => {
  if (subject === 1) {
    return productFieldId;
  }
  return deliveryFieldId;
};
